import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export type Language = 'ja' | 'en';

const STORAGE_KEY = 'ea-notebookllm-language';

// Translations for EA NotebookLLM
const translations: { [section: string]: { [key: string]: { ja: string; en: string } } } = {
  auth: {
    authenticating: { ja: '認証中...', en: 'Authenticating...' },
    redirecting: { ja: 'ログインページへリダイレクト中...', en: 'Redirecting to login...' },
  },
  header: {
    title: { ja: 'EA NotebookLLM', en: 'EA NotebookLLM' },
    logout: { ja: 'ログアウト', en: 'Logout' },
    user: { ja: 'ユーザー', en: 'User' },
  },
  notebook: {
    title: { ja: 'EA Notebook', en: 'EA Notebook' },
    description: { ja: 'TOGAF/ADMフレームワークに基づくドキュメント分析', en: 'Document analysis based on TOGAF/ADM framework' },
    createNew: { ja: '新規作成', en: 'Create new' },
    newNotebook: { ja: '新しいノートブック', en: 'Create New Notebook' },
    notebookName: { ja: 'ノートブック名', en: 'Notebook name' },
    cancel: { ja: 'キャンセル', en: 'Cancel' },
    create: { ja: '作成', en: 'Create' },
    delete: { ja: 'このノートブックを削除しますか？', en: 'Delete this notebook?' },
    sources: { ja: 'ソース', en: 'sources' },
    addSources: { ja: 'ソースを追加', en: 'Add sources' },
    savedSources: { ja: '保存されたソースがここに表示されます', en: 'Saved sources will appear here' },
    analyze: { ja: 'TOGAF分析実行', en: 'Run TOGAF Analysis' },
    analyzing: { ja: '分析中...', en: 'Analyzing...' },
    askQuestions: { ja: 'ソースについて質問してください', en: 'Ask questions about your sources' },
    togafBased: { ja: 'TOGAFフレームワークに基づいて回答します', en: 'Answers will be based on TOGAF framework' },
    typeMessage: { ja: 'メッセージを入力...', en: 'Type a message...' },
    uploadFiles: { ja: 'ファイル', en: 'Upload files' },
    websites: { ja: 'ウェブサイト', en: 'Websites' },
    copiedText: { ja: 'テキスト', en: 'Copied text' },
    dropOrClick: { ja: 'ファイルをドロップまたはクリックしてアップロード', en: 'Drop files or click to upload' },
    pasteText: { ja: 'テキストを貼り付け...', en: 'Paste text here...' },
    add: { ja: '追加', en: 'Add' },
    adding: { ja: '追加中...', en: 'Adding...' },
    uploadFailed: { ja: 'ファイルのアップロードに失敗しました', en: 'Failed to upload file' },
    analysisFailed: { ja: '分析中にエラーが発生しました。再度お試しください。', en: 'An error occurred during analysis. Please try again.' },
    errorOccurred: { ja: 'エラーが発生しました', en: 'An error occurred' },
    addNote: { ja: 'ノートを追加', en: 'Add note' },
    phases: { ja: 'フェーズ', en: 'Phases' },
    outputs: { ja: '出力', en: 'Outputs' },
    addSourcePrompt: { ja: 'ソースを追加してください', en: 'Add sources to get started' },
    uploadPrompt: { ja: 'PDFやドキュメントをアップロードしてTOGAF分析を実行', en: 'Upload PDFs or documents to run TOGAF analysis' },
    runAnalysisPrompt: { ja: 'ソースを追加して「TOGAF分析実行」を押すと、AIが出力を生成します', en: 'Add sources and run TOGAF Analysis to generate AI outputs' },
    studio: { ja: 'スタジオ', en: 'Studio' },
    notes: { ja: 'ノート', en: 'Notes' },
    noNotes: { ja: 'まだノートがありません', en: 'No notes yet' },
    notePlaceholder: { ja: 'ノートを書く...', en: 'Write a note...' },
    save: { ja: '保存', en: 'Save' },
    edit: { ja: '編集', en: 'Edit' },
    deleteNoteConfirm: { ja: 'このノートを削除しますか？', en: 'Delete this note?' },
    saveToNote: { ja: 'ノートに保存', en: 'Save to note' },
    savedToNote: { ja: 'ノートに保存しました', en: 'Saved to notes' },
    copy: { ja: 'コピー', en: 'Copy' },
    copied: { ja: 'コピーしました', en: 'Copied' },
    download: { ja: 'ダウンロード', en: 'Download' },
    clearChat: { ja: 'チャットをクリア', en: 'Clear chat' },
    clearChatConfirm: { ja: 'チャット履歴を削除しますか？', en: 'Clear the chat history?' },
    thinking: { ja: '考え中...', en: 'Thinking...' },
    suggestedQuestions: { ja: 'おすすめの質問', en: 'Suggested questions' },
    analyzed: { ja: '分析済み', en: 'Analyzed' },
    notAnalyzed: { ja: '未分析', en: 'Not analyzed' },
    reanalyze: { ja: '再分析', en: 'Re-analyze' },
    analyzingSource: { ja: '分析中...', en: 'Analyzing...' },
    sourceDetails: { ja: 'ソース詳細', en: 'Source details' },
    aiSummary: { ja: 'AIサマリー', en: 'AI Summary' },
    keyPoints: { ja: 'キーポイント', en: 'Key points' },
    risks: { ja: 'リスク', en: 'Risks' },
    recommendations: { ja: '推奨事項', en: 'Recommendations' },
    contentPreview: { ja: 'コンテンツプレビュー', en: 'Content preview' },
    noContent: { ja: '抽出されたテキストがありません', en: 'No extracted text' },
    purpose: { ja: '目的', en: 'Purpose' },
    tasks: { ja: 'タスク', en: 'Tasks' },
    who: { ja: '担当者', en: 'Who' },
    deliverablesLabel: { ja: '成果物', en: 'Deliverables' },
    relatedSources: { ja: '関連ドキュメント', en: 'Related sources' },
    noPhaseSources: { ja: 'このフェーズに分類されたソースはまだありません', en: 'No sources classified into this phase yet' },
    uploading: { ja: 'アップロード中', en: 'Uploading' },
    selectFolder: { ja: '📁 フォルダをアップロード', en: '📁 Upload a folder' },
    folderHint: { ja: 'フォルダ内のすべてのファイルが追加されます', en: 'All files inside the folder will be added' },
    filesQueued: { ja: '件のファイルをアップロード中...', en: ' file(s) uploading...' },
    filesSkipped: { ja: '件をスキップ（25MB超または空）', en: ' skipped (over 25MB or empty)' },
    tooManyFiles: { ja: '一度にアップロードできるのは100ファイルまでです', en: 'Maximum 100 files per upload' },
    anyFileHint: { ja: 'その他のファイルもテキストとして取り込めます', en: 'Other file types are imported as text' },
    metadata: { ja: 'メタデータ', en: 'Metadata' },
    metaPath: { ja: 'パス', en: 'Path' },
    metaFormat: { ja: '形式', en: 'Format' },
    metaSize: { ja: 'サイズ', en: 'Size' },
    metaAdded: { ja: '追加日時', en: 'Added' },
    metaChars: { ja: '抽出文字数', en: 'Extracted chars' },
    metaMime: { ja: 'MIMEタイプ', en: 'MIME type' },
    analysisDone: { ja: '完了', en: 'Done' },
    analysisError: { ja: 'エラー', en: 'Error' },
    rename: { ja: '名前を変更', en: 'Rename' },
    deleteNotebook: { ja: '削除', en: 'Delete' },
    notebookDescription: { ja: '説明（任意）', en: 'Description (optional)' },
    sendHint: { ja: 'Enterで送信 / Shift+Enterで改行', en: 'Enter to send / Shift+Enter for newline' },
    analysisComplete: { ja: '✅ TOGAF分析が完了しました。スタジオパネルで全出力を確認できます。', en: '✅ TOGAF analysis complete. See the Studio panel for all outputs.' },
    welcomeChat: { ja: 'ソースの内容について何でも質問してください。回答はアップロードされたソースに基づきます。', en: 'Ask anything about your sources. Answers are grounded in your uploaded documents.' },
    lastUpdated: { ja: '更新', en: 'Updated' },
    deleteSourceConfirm: { ja: 'このソースを削除しますか？', en: 'Delete this source?' },
    close: { ja: '閉じる', en: 'Close' },
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (section: string, key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

const getInitialLanguage = (): Language => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ja') return stored;
  } catch {}
  return 'ja';
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {}
  }, []);

  const t = useCallback((section: string, key: string): string => {
    const sectionData = translations[section];
    if (!sectionData) return key;
    const entry = sectionData[key];
    if (!entry) return key;
    return entry[language] || entry['ja'] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
